import { Box, Flex, Grid, Radio, Text } from "@chakra-ui/react";
import { useFile } from "../../../hooks/file.hooks";
import SelectDropdown, {
	type TSelectDropdownOption,
} from "../../../components/inputs/SelectDropdown";
import type { VariantType } from "../../../types/variant.types";
import { cn } from "../../../lib/utils";
import _ from "lodash";
import { useMaterialByLabel } from "../../../hooks/material.hooks";
import BrandFullCheckIcon from "../../../components/icons/BrandFullCheckIcon";

export type EarSectionFormSectionOptions = {
	key: string;
	disabled: boolean;
	fileId?: number | string | null;
} & TSelectDropdownOption;

/**
 * Display form section. Either as a list of checkpoints are a a dropdown selection list.
 */
export const EarSectionFormSection = ({
	options,
	value,
	type,
	setValue,
}: {
	options?: EarSectionFormSectionOptions[];
	value?: TSelectDropdownOption;
	type?: "dropdown" | "radio" | "MATERIAL" | VariantType;
	setValue?: (value: TSelectDropdownOption) => void;
}) => {
	const handleSetValue = (option: EarSectionFormSectionOptions) => {
		if (option.disabled === false) {
			if (setValue) {
				setValue(option);
			}
		}
	};
	const DropdownVariant = () => {
		return (
			<SelectDropdown
				value={value}
				disabled={
					Array.isArray(options) && _.every(options.map((opt) => opt.disabled))
				}
			>
				{Array.isArray(options) &&
					options
						.sort((a, b) => (a.disabled ? 1 : 0) - (b.disabled ? 1 : 0))
						.map((option) => {
							return (
								<SelectDropdown.Option
									key={option.key}
									onClick={() => handleSetValue(option)}
									disabled={option.disabled}
								>
									{option.label}
								</SelectDropdown.Option>
							);
						})}
			</SelectDropdown>
		);
	};
	const RadioVariant = () => {
		return (
			<Grid gridTemplateColumns={"1fr 1fr"}>
				{Array.isArray(options) &&
					options.map((option) => {
						return (
							<Box onClick={() => handleSetValue(option)} key={option.key}>
								<Radio
									key={option.key}
									isDisabled={option.disabled}
									isChecked={option.value === value?.value}
									colorScheme="brand"
								>
									{option.label}
								</Radio>
							</Box>
						);
					})}
			</Grid>
		);
	};
	const ImagePerItemsVariant = () => {
		const ImagePerItemVariantOption = ({
			option,
		}: {
			option: EarSectionFormSectionOptions;
		}) => {
			const { data: file } = useFile({ id: option.fileId });

			return (
				<Flex
					direction="column"
					onClick={(e) => {
						e.stopPropagation();
						e.preventDefault();
						if (option.disabled) return;

						handleSetValue(option);
					}}
					backgroundColor={option.disabled ? "gray.200" : "white"}
					border={
						option.disabled
							? undefined
							: option.value === value?.value
								? "2px solid"
								: "1px solid"
					}
					borderColor={
						option.disabled
							? undefined
							: option.value === value?.value
								? "brand.700"
								: "#ccc"
					}
					rounded="lg"
					p={4}
					gap={4}
					justifyContent="center"
					alignItems="center"
					cursor={option.disabled ? "not-allowed" : "pointer"}
				>
					<img
						src={file?.url}
						alt={file?.fileName}
						className="w-[140px] max-w-[140px] aspect-square"
					/>
					{option.label}
				</Flex>
			);
		};
		return (
			<Grid gridTemplateColumns={"1fr 1fr"} gap={4}>
				{Array.isArray(options) &&
					options.map((option) => {
						return (
							<ImagePerItemVariantOption key={option.key} option={option} />
						);
					})}
			</Grid>
		);
	};
	const SliderVariant = () => {
		const isActive = (option: EarSectionFormSectionOptions) => {
			return option.value === value?.value;
		};

		if (!options) return null;
		return (
			<div
				style={{
					position: "relative",
					width: "100%",
					display: "grid",
					gridTemplateColumns: `repeat(${options.length.toString()},1fr)`,
				}}
			>
				{options.map((option) => (
					<Box
						key={option.key}
						zIndex={10}
						className="grid grid-rows-[16px_1fr] justify-center justify-items-center items-center gap-2 cursor-pointer"
						onClick={(e) => {
							e.stopPropagation();
							e.preventDefault();
							handleSetValue(option);
						}}
					>
						<div
							className={cn(
								isActive(option)
									? "bg-brand-500 w-4 h-4"
									: "bg-gray-400 w-2.5 h-2.5",
								"rounded-full items-center",
							)}
						></div>
						<span
							className={cn(
								isActive(option) ? "text-brand-500" : "text-gray-400",
							)}
						>
							{option.label}
						</span>
					</Box>
				))}
				<div className="bg-gray-300 w-full absolute left-0 right-0 h-1 top-1.5"></div>
			</div>
		);
	};

	const MaterialVariant = () => {
		const isActive = (option: EarSectionFormSectionOptions) => {
			return option.value === value?.value;
		};
		const groups = [
			"Résine",
			"20 shore",
			"25 shore",
			"30 shore",
			"35 shore",
			"40 shore",
			"45 shore",
			"50 shore",
			"55 shore",
			"60 shore",
		];
		const groupsOptions = Object.fromEntries(
			groups.map((group) => [
				group,
				options
					?.filter((option) => option.label.startsWith(group))
					.map((option) => ({
						...option,
						label: option.label.replace(group, "").trim(),
						originalLabel: option.label,
					})) ?? [],
			]),
		);
		const MaterialIcon = ({ label }: { label: string }) => {
			const materialState = useMaterialByLabel(label);
			const fileState = useFile({ id: materialState?.fileId });
			const className = "w-10 h-10 aspect-square";

			if (
				!materialState ||
				!fileState ||
				typeof fileState.data?.url !== "string"
			)
				return (
					<img
						className={className}
						src="https://placehold.co/10x10"
						alt={label}
					/>
				);
			return (
				<img className={className} src={fileState.data?.url} alt={label} />
			);
		};
		if (!options) return null;
		return (
			<Flex direction={"column"}>
				{Object.entries(groupsOptions)
					.filter(([, options]) => options.length > 0)
					.map(([group, options]) => {
						return (
							<Box className="gap-2 py-2" key={group}>
								<Text
									fontSize="12px"
									fontWeight={600}
									className="text-gray-500"
								>
									{group}
								</Text>
								<div className="grid grid-cols-4 gap-4">
									{options.map((option) => (
										<Flex
											direction="column"
											className="justify-center items-center gap-4"
											key={option.value}
											onClick={(e) => {
												e.stopPropagation();
												e.preventDefault();
												handleSetValue(option);
											}}
										>
											<div className="relative">
												<MaterialIcon label={option.originalLabel} />
												{isActive(option) && (
													<BrandFullCheckIcon className="absolute right-0 top-0 text-brand-700!" />
												)}
											</div>
											<Text
												fontSize="14px"
												fontWeight={500}
												className={
													isActive(option) ? "text-brand-700" : "text-gray-700"
												}
											>
												{option.label}
											</Text>
										</Flex>
									))}
								</div>
							</Box>
						);
					})}
			</Flex>
		);
	};

	let defaultVariant = DropdownVariant;
	if (Array.isArray(options) && options.length < 6) {
		defaultVariant = RadioVariant;
	}
	if (typeof type !== "string") {
		return defaultVariant();
	}
	if (type.toUpperCase() === "DROPDOWN") {
		return <DropdownVariant />;
	}
	if (type.toUpperCase() === "RADIO") {
		return <RadioVariant />;
	}
	if (type.toUpperCase() === "IMAGE_PER_ITEM") {
		return <ImagePerItemsVariant />;
	}
	if (type.toUpperCase() === "CURSOR") {
		return <SliderVariant />;
	}
	if (type.toUpperCase() === "MATERIAL") {
		return <MaterialVariant />;
	}
	return defaultVariant();
};
